import { phaseStandings, type GroupsConfig, type Phase } from '@airmail/engine';
import { useCompetition, useSync, useTurnier } from '@/store/TournamentProvider';
import { alertDialog, confirmDialog } from '@/ui/dialogs';
import { useT } from '@/i18n';

/**
 * Eine Phase abschließen — an einer Stelle für alle Seiten, die den Knopf
 * zeigen (Auslosung, KO-Runde, KO-Runden, Spiele, Start).
 *
 * Anlass: Die Knöpfe standen nur auf „Auslosung" und „KO-Runde", und dort ist
 * am Turniertag niemand mehr — gespielt wird unter „Spiele". Der Hook rechnet
 * den Stand der Phase, prüft die Los-Fälle, fragt bei offenen Spielen nach und
 * sagt, wohin es danach geht (`ziel`). Ob gesprungen wird, entscheidet die
 * Seite: „Auslosung" bleibt stehen (nur im Liga-Modus geht es zur Tabelle),
 * „Spiele" und „Start" springen immer.
 */
export interface PhaseAbschluss {
  /** beendet oder kampflos gewertet */
  gewertet: number;
  laufend: number;
  /** wartet noch (offen oder bereit) */
  offen: number;
  /** alle Spiele der Phase ohne die abgesagten */
  gesamt: number;
  /** nichts läuft mehr, nichts wartet mehr — und mindestens ein Spiel ist gewertet */
  fertig: boolean;
  /** danach folgt keine Phase mehr: „Turnier beenden" statt „Phase abschließen" */
  letztePhase: boolean;
  /** Gruppenphase: auch mit offenen Spielen abschließbar (nach Rückfrage, offene werden abgesagt) */
  erzwingbar: boolean;
  /** KO-Runden ohne gestartete Runde lassen sich nicht abschließen */
  moeglich: boolean;
  /** Ein Helfer darf nicht — der Server lässt `phase.finish` nicht durch (HELFER_AKTIONEN) */
  darf: boolean;
  /** Wohin danach: die Seite der nächsten Phase, sonst die Tabelle (Endstand) */
  ziel: string;
  /** KO-Runden: nach einer fertigen Runde ist „Nächste Runde starten" die andere Wahl */
  weiter?: string;
  /** Rückfragen inklusive; `true`, wenn die Phase jetzt abgeschlossen ist */
  abschliessen(): Promise<boolean>;
}

/** Seite, auf der eine Phase ausgelost bzw. gestartet wird */
function seiteVon(phase: Phase): string {
  return phase.type === 'single_elim' ? '../ko' : phase.type === 'ko_rounds' ? '../ko-runden' : '../vorrunde';
}

export function usePhaseAbschluss(phase: Phase): PhaseAbschluss {
  const t = useT();
  const { tournament, dispatch } = useTurnier();
  const sync = useSync();
  const comp = useCompetition();

  let gewertet = 0;
  let laufend = 0;
  let offen = 0;
  let gesamt = 0;
  for (const m of phase.matches) {
    if (m.status === 'cancelled') continue;
    gesamt++;
    if (m.status === 'finished' || m.status === 'walkover') gewertet++;
    else if (m.status === 'running') laufend++;
    else offen++;
  }
  const naechste = comp.phases.find((p) => p.order === phase.order + 1);
  const letztePhase = !naechste;
  const erzwingbar = phase.type === 'groups';
  const moeglich = phase.type !== 'ko_rounds' || (phase.koRounds?.length ?? 0) > 0;
  const darf = sync.cfg?.role !== 'helfer';
  const fertig = offen === 0 && laufend === 0 && gewertet > 0;

  const abschliessen = async (): Promise<boolean> => {
    if (!darf || !moeglich) return false;
    if (phase.type === 'groups') {
      // Los-Fälle auf den Plätzen, die zählen — die Engine lehnt sonst ab, aber mit der Gruppe im Text ist es klarer
      const cfg = phase.config as GroupsConfig;
      const lotOpen = phaseStandings(tournament, phase.id).filter((x) => x.rows.some((r) => r.needsLot && r.rank <= cfg.qualifiersPerGroup + 1));
      if (lotOpen.length > 0) {
        await alertDialog(t('Erst losen: Gruppe {g}', { g: lotOpen.map((x) => x.group.name).join(', ') }));
        return false;
      }
    }
    const pending = offen + laufend;
    if (pending > 0) {
      // KO: erst alle Spiele werten — der Knopf ist dort ohnehin gesperrt
      if (!erzwingbar) return false;
      if (!(await confirmDialog(t('{n} Spiele sind noch offen. Trotzdem abschließen? Offene Spiele werden abgesagt.', { n: pending }), { ok: t('Trotzdem abschließen'), danger: true }))) return false;
    } else if (phase.type === 'ko_rounds' && !(await confirmDialog(t('Phase abschließen? Danach lässt sich keine Runde mehr starten.'), { ok: t('Abschließen') }))) return false;
    // Laufende Spiele meldet die Engine selbst („n Spiel(e) laufen noch") — als Toast
    return dispatch({ type: 'phase.finish', phaseId: phase.id, force: pending > 0 }).ok;
  };

  const ziel = naechste ? seiteVon(naechste) : '../tabelle';
  const out: PhaseAbschluss = { gewertet, laufend, offen, gesamt, fertig, letztePhase, erzwingbar, moeglich, darf, ziel, abschliessen };
  if (phase.type === 'ko_rounds' && phase.status !== 'finished') out.weiter = seiteVon(phase);
  return out;
}
